import DesignTokens from '@tableau/design-tokens';
import { SitesMultipleClusterIcon } from '@tableau/icons/SitesMultipleClusterIcon';
import { ActivitySpinnerSize, ActivitySpinnerWidget, FilteringList, ListItem, TabStyles, Tooltip } from '@tableau/widgets';
import React, { useState } from 'react';
import { style } from 'typestyle';
import _ from 'underscore';

import { ReactActionDialogBodyProps } from '~commonInfra/dialogs/ReactActionDialog';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Messages } from '~src/codegen/Localize';
import { cssClass } from '~src/style';
import { applyColor }from '~src/style/ColorService';

import { SettingsStyles } from '../../../styles/CommonStyles';
import { IFetchAllClustersResult } from '../../serverApi/requests/FetchAllClusterstRequest';
import { BaasEdgePoolAndCluster } from '../../serverApi/resources/BaasEdgePoolAndCluster';
import { getGeneralErrorMessage } from '../utils/EdgePoolErrorUtilities';


export type PropsForAssignClusterDialog = ReactActionDialogBodyProps<
   AssignClusterDialogProps,
   AssignClusterDialogState
>;

export type AssignClusterDialogProps = Readonly<{
  description: string;
  poolId: string;
  siteId: string;
}>

export type AssignClusterDialogState = Readonly<{
  clusterId: string;
  clusterName: string;
  hasError: boolean;
}>

export function AssignClusterDialogBody(props: PropsForAssignClusterDialog)  {
  const descriptionDivClassName = `${SettingsStyles.settingsGroupDescription}`;

  const [searchText, setSearchText] = React.useState('');
  const [allClustersItems, setAllClustersItems] = useState([]);
  const [filterItems, setFilterItems] = useState([]);
  const [showSpinner, setShowSpinner] = React.useState(true);
  const [isError, setIsError] = React.useState(false);

  const onSelectCluster = React.useCallback(
    (value: string) => {
      const item = allClustersItems.find((item) => item.value === value);
      if (item) {
        props.updateDialogState({ clusterId: item.value });
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.updateDialogState, allClustersItems]
  );

  const onClusterPickerFilterChange = React.useCallback(
    (value: string) => {
      setSearchText(value);
    },
    []
  );

  React.useEffect(() => {
    let ignore = false;
    if (searchText === '' || allClustersItems.length === 0) {
      setFilterItems(allClustersItems);
    } else {
      const filterResult = _.filter(allClustersItems, item => item.displayName.toLowerCase().includes(searchText.toLowerCase()));
      setFilterItems(filterResult);
    }
    return () => {ignore = true;};
  },[allClustersItems, searchText]),


  React.useEffect(() => {
    BaasEdgePoolAndCluster.fetchAllClusters({
      siteId: props.siteId,
    })
    .then((result: IFetchAllClustersResult) => {
      if(result.success) {
        const newClusterItems: ListItem[] = result.success.map(clusterWithSite => ({
          content:  generateItemContent(clusterWithSite.displayName, clusterWithSite.shared),
          displayName: clusterWithSite.displayName,
          value: clusterWithSite.clusterId.toString()
        }));
        setAllClustersItems(newClusterItems);
        setShowSpinner(false);
      }
      if(result.errors) {
        ToasterService.instance.pop({
          type: ToastType.Error,
          text: getGeneralErrorMessage(result.errors[0].code, result.errors[0].message)
        });
        setShowSpinner(false);
        setIsError(true);
      }
    });
  }, [props.siteId]);

  /* eslint-disable jsx-a11y/no-autofocus */
  return (
    <>
      <div className={descriptionDivClassName} data-tb-test-id={'select-cluste-details'}>
          <span>
                  {props.description}
          </span>
      </div>
      <div style={{ height: 300 }}>
        <FilteringList
          items={filterItems}
          onSelect={onSelectCluster}
          searchPlaceholder={Messages.search()}
          testId="filtering-list-assign-cluster"
          style={{height: '100%', maxHeight:'300px', width: 'auto', border: '1px solid ' + applyColor(DesignTokens.color.elevation.border.default.default, '#b2b2b2')}}
          onFilterChange={onClusterPickerFilterChange}
          searchText={searchText}
          autoFocus={true}
          inputTestId="assign-cluster-picker-filter"
          listTestId="assign-cluster-picker-list"
        >
          {!showSpinner && (filterItems.length === 0 || isError) &&
            <div className={noResults} data-tb-test-id="set-owner-no-results">
              {hasSearchText(searchText)? Messages.noMatchingResults() : Messages.dataConnectSiteSettingsClusterNoClusterAvailable()}
            </div>
          }
          {showSpinner &&
            <div className={spinner}>
              <ActivitySpinnerWidget
                spinnerSize={ActivitySpinnerSize.Large}
                testId={'owner-picker'}
              />
            </div>
          }
        </FilteringList>
      </div>
    </>
  );
}

function hasSearchText(text: string): boolean {
  return !_.isUndefined(text) && text.length !== 0;
}

function generateItemContent(text: string, isShared: boolean): React.ReactNode {
    return (<><span>{text}</span><span style={{paddingLeft: 4, verticalAlign: 'text-top'}}>
      { isShared && <>
        <Tooltip children={<SitesMultipleClusterIcon size={12} alt={''} />}
             tooltipContent={Messages.dataConnectSiteSettingsClusterShared()}
             position={Tooltip.defaultProps.position!}
             afterDelay={false}
             followCursor={false} />
        </>}
    </span></>);
}


const fieldClass = style({ marginBottom: TabStyles.Sizing.BaseUnit * 5 });

const cammandLIneClass = style({ marginTop: 10, display: 'block', fontWeight: 'bold' });


const textFieldClass = cssClass('edit-pool-dialog', 'field', {
  width: '100%'
});

export const errorClass = style({ marginTop: TabStyles.Spacing.DefaultSpace, color: applyColor(DesignTokens.color.font.default.error, TabStyles.Colors.AttentionDark) });

const spinner = cssClass('set-owner-dialog', 'spinner', {
  marginTop: 18,
  textAlign: 'center'
});

const noResults = cssClass('set-owner-dialog', 'no-results', {
  fontSize: '15px',
  marginTop: 18,
  padding: 6,
  lineHeight: '23px',
  textAlign: 'center',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});
